"use client"

import { useState } from "react"
import { X, Star } from "lucide-react"

interface Props {
  subjectId: string
  subjectName: string
  existingReview?: { id: string; rating: number; content: string } | null
  projectId?: string | null   // if set, review will be auto-verified via completed project
  onClose: () => void
  onSaved: (review: ReviewData) => void
}

export interface ReviewData {
  id: string
  rating: number
  content: string
  verified: boolean
  verifiedAt: string | null
  deleteRequestAuthor: boolean
  deleteRequestSubject: boolean
  createdAt: string
  author: { id: string; name: string; userType: string; location: string | null }
}

export default function ReviewModal({ subjectId, subjectName, existingReview, projectId, onClose, onSaved }: Props) {
  const [rating, setRating] = useState(existingReview?.rating ?? 0)
  const [hover, setHover] = useState(0)
  const [content, setContent] = useState(existingReview?.content ?? "")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const isEdit = !!existingReview

  async function handleSubmit() {
    if (rating === 0) { setError("Please select a star rating"); return }
    if (!content.trim()) { setError("Please write your review"); return }
    setSaving(true)
    setError("")

    const url = isEdit ? `/api/reviews/${existingReview!.id}` : `/api/reviews/user/${subjectId}`
    const method = isEdit ? "PATCH" : "POST"

    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rating, content, ...(projectId ? { projectId } : {}) }),
    })
    const data = await res.json()
    setSaving(false)

    if (!res.ok) { setError(data.error || "Something went wrong"); return }
    onSaved(data)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? "Edit Your Review" : `Review ${subjectName}`}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Star rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                  className="p-0.5"
                >
                  <Star
                    className="w-8 h-8 transition-colors"
                    fill={(hover || rating) >= star ? "#F59E0B" : "none"}
                    stroke={(hover || rating) >= star ? "#F59E0B" : "#D1D5DB"}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Review text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Review
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder={`Share your experience with ${subjectName}…`}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{content.length}/1000</p>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {/* Info about verified reviews */}
          {!isEdit && projectId && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-start gap-2">
              <span className="text-emerald-600 text-base flex-shrink-0">✓</span>
              <p className="text-xs text-emerald-800 leading-relaxed">
                <span className="font-semibold">Project-Verified</span> — this review is automatically verified because it&apos;s tied to a completed project. The bid amount serves as the payment on record.
              </p>
            </div>
          )}
          {!isEdit && !projectId && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <p className="text-xs text-amber-800 leading-relaxed">
                <span className="font-semibold">Free reviews</span> are visible immediately.{" "}
                After posting, the reviewed person can pay <span className="font-semibold">$10</span> to add a{" "}
                <span className="font-semibold">✓ Verified</span> badge — signalling the job actually happened.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Post Review"}
          </button>
        </div>
      </div>
    </div>
  )
}
